import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { getSettings } from "../../../services/settingsService";

// Move CodeEditor component outside to prevent recreation on every render
const CodeEditor = ({ language, value, onChange, placeholder, height = "400px", customCodeSettings }) => {
    const editorRef = useRef(null);
    const [isEditorReady, setIsEditorReady] = useState(false);

    useEffect(() => {
        // Simple code editor implementation
        const textarea = editorRef.current;
        if (textarea) {
            textarea.style.fontFamily = 'Monaco, Menlo, "Ubuntu Mono", monospace';
            textarea.style.fontSize = `${customCodeSettings.editor_font_size}px`;
            textarea.style.lineHeight = '1.5';
            textarea.style.tabSize = customCodeSettings.editor_tab_size;
            textarea.style.whiteSpace = customCodeSettings.editor_word_wrap ? 'pre-wrap' : 'pre';
            setIsEditorReady(true);
        }
    }, [customCodeSettings.editor_font_size, customCodeSettings.editor_tab_size, customCodeSettings.editor_word_wrap]);

    return (
        <div className="code-editor-container" style={{ position: 'relative' }}>
            <div className="code-editor-header" style={{
                background: '#f8f9fa',
                padding: '8px 12px',
                border: '1px solid #dee2e6',
                borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                fontSize: '12px',
                color: '#6c757d',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <span>{language.toUpperCase()}</span>
                <div className="editor-options">
                    <span style={{ marginRight: '10px' }}>Lines: {value.split('\n').length}</span>
                    <span>Chars: {value.length}</span>
                </div>
            </div>
            <textarea
                ref={editorRef}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                style={{
                    width: '100%',
                    height: height,
                    border: '1px solid #dee2e6',
                    borderRadius: '0 0 4px 4px',
                    padding: '12px',
                    fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
                    fontSize: `${customCodeSettings.editor_font_size}px`,
                    lineHeight: '1.5',
                    tabSize: customCodeSettings.editor_tab_size,
                    whiteSpace: customCodeSettings.editor_word_wrap ? 'pre-wrap' : 'pre',
                    resize: 'vertical',
                    outline: 'none',
                    background: customCodeSettings.editor_theme === 'vs-dark' ? '#1e1e1e' : '#ffffff',
                    color: customCodeSettings.editor_theme === 'vs-dark' ? '#d4d4d4' : '#333333'
                }}
                spellCheck={false}
            />
        </div>
    );
};

const CustomCode = ({ customCode = {}, setCustomCode }) => {
    const [customCodeSettings, setCustomCodeSettings] = useState({
        // Custom Code Settings
        custom_css: '',
        custom_js: '',
        custom_html: '',
        custom_head_code: '',
        custom_body_code: '',
        custom_footer_code: '',
        
        // Code Editor Settings
        editor_theme: 'vs-dark', // 'vs', 'vs-dark', 'hc-black'
        editor_font_size: 14,
        editor_tab_size: 2,
        editor_word_wrap: true,
        editor_line_numbers: true,
        editor_minimap: true,
        editor_auto_complete: true,
        editor_bracket_matching: true,
        editor_indent_guides: true,
        
        // Code Execution Settings
        css_minify: false,
        js_minify: false,
        html_minify: false,
        css_compress: false,
        js_compress: false,
        
        // Security Settings
        allow_external_scripts: false,
        allow_inline_scripts: true,
        allow_external_styles: false,
        allow_inline_styles: true,
        csp_enabled: true,
        
        // Performance Settings
        css_async_load: false,
        js_async_load: true,
        js_defer_load: false,
        css_priority: 'normal', // 'high', 'normal', 'low'
        js_priority: 'normal',
        
        // Development Settings
        dev_mode: false,
        code_validation: true,
        syntax_checking: true,
        error_reporting: true,
        debug_mode: false
    });

    const [activeTab, setActiveTab] = useState('css');
    const [isLoading, setIsLoading] = useState(false);
    const editorRefs = useRef({});

    useEffect(() => {
        if (customCode && Object.keys(customCode).length > 0) {
            setCustomCodeSettings(prev => ({ ...prev, ...customCode }));
        }
    }, [customCode]);

    useEffect(() => {
        getSettings().then(settings => {
            setCustomCode(settings);
        });
    }, []);

    const handleInputChange = useCallback((field, value) => {
        const updatedSettings = {
            ...customCodeSettings,
            [field]: value
        };
        setCustomCodeSettings(updatedSettings);
        setCustomCode(updatedSettings);
    }, [customCodeSettings, setCustomCode]);

    const handleCodeChange = useCallback((language, value) => {
        const field = `custom_${language}`;
        handleInputChange(field, value);
    }, [handleInputChange]);

    const handleSave = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            // Here you would typically call an API to save custom code
            // For now, we'll just simulate success
            setTimeout(() => {
                toast.success("Custom code saved successfully");
                setIsLoading(false);
            }, 1000);
        } catch (error) {
            console.error("Error saving custom code:", error);
            toast.error(error.message || "Failed to save custom code");
            setIsLoading(false);
        }
    };


    const TabButton = ({ id, label, active, onClick }) => (
        <button
            type="button"
            className={`btn ${active ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => onClick(id)}
            style={{
                marginRight: '5px',
                marginBottom: '10px',
                borderRadius: '4px'
            }}
        >
            {label}
        </button>
    );

    return (
        <div className="dc-tabscontent tab-content">
            <div className="dc-personalskillshold tab-pane active fade show" id="dc-skills">
                <div className="dc-tabsinfo">
                    <div className="dc-tabscontenttitle">
                        <h3>Custom Code Editor</h3>
                        <p className="text-muted">Add custom CSS, JavaScript, and HTML code to your medical platform with a professional code editor.</p>
                    </div>
                </div>

                {/* Code Editor Tabs */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h4>Code Editor</h4>
                    </div>
                    <div className="dc-formtheme dc-userform">
                        <div className="code-tabs" style={{ marginBottom: '20px' }}>
                            <TabButton
                                id="css"
                                label="CSS"
                                active={activeTab === 'css'}
                                onClick={setActiveTab}
                            />
                            <TabButton
                                id="js"
                                label="JavaScript"
                                active={activeTab === 'js'}
                                onClick={setActiveTab}
                            />
                            <TabButton
                                id="html"
                                label="HTML"
                                active={activeTab === 'html'}
                                onClick={setActiveTab}
                            />
                            <TabButton
                                id="head"
                                label="Head Code"
                                active={activeTab === 'head'}
                                onClick={setActiveTab}
                            />
                            <TabButton
                                id="body"
                                label="Body Code"
                                active={activeTab === 'body'}
                                onClick={setActiveTab}
                            />
                            <TabButton
                                id="footer"
                                label="Footer Code"
                                active={activeTab === 'footer'}
                                onClick={setActiveTab}
                            />
                        </div>

                        {activeTab === 'css' && (
                            <div className="form-group">
                                <label className="dc-label">Custom CSS</label>
                                <CodeEditor
                                    language="css"
                                    value={customCodeSettings.custom_css || ''}
                                    onChange={(value) => handleCodeChange('css', value)}
                                    placeholder="/* Enter your custom CSS code here */&#10;.custom-class {&#10;    color: #007bff;&#10;    font-size: 16px;&#10;}"
                                    customCodeSettings={customCodeSettings}
                                />
                                <small className="form-text text-muted">
                                    Add custom CSS styles for your site. This code will be injected into the &lt;head&gt; section.
                                </small>
                            </div>
                        )}

                        {activeTab === 'js' && (
                            <div className="form-group">
                                <label className="dc-label">Custom JavaScript</label>
                                <CodeEditor
                                    language="javascript"
                                    value={customCodeSettings.custom_js || ''}
                                    onChange={(value) => handleCodeChange('js', value)}
                                    placeholder="// Enter your custom JavaScript code here&#10;document.addEventListener('DOMContentLoaded', function() {&#10;    console.log('Custom JS loaded');&#10;});"
                                    customCodeSettings={customCodeSettings}
                                />
                                <small className="form-text text-muted">
                                    Add custom JavaScript functionality. This code will be injected before the closing &lt;/body&gt; tag.
                                </small>
                            </div>
                        )}

                        {activeTab === 'html' && (
                            <div className="form-group">
                                <label className="dc-label">Custom HTML</label>
                                <CodeEditor
                                    language="html"
                                    value={customCodeSettings.custom_html || ''}
                                    onChange={(value) => handleCodeChange('html', value)}
                                    placeholder="<!-- Enter your custom HTML code here -->&#10;&lt;div class=&quot;custom-widget&quot;&gt;&#10;    &lt;h3&gt;Custom Content&lt;/h3&gt;&#10;    &lt;p&gt;Your custom HTML here&lt;/p&gt;&#10;&lt;/div&gt;"
                                    customCodeSettings={customCodeSettings}
                                />
                                <small className="form-text text-muted">
                                    Add custom HTML elements. This code will be injected into the page body.
                                </small>
                            </div>
                        )}

                        {activeTab === 'head' && (
                            <div className="form-group">
                                <label className="dc-label">Head Code</label>
                                <CodeEditor
                                    language="html"
                                    value={customCodeSettings.custom_head_code || ''}
                                    onChange={(value) => handleCodeChange('head_code', value)}
                                    placeholder="<!-- Enter code to be placed in &lt;head&gt; section -->&#10;&lt;meta name=&quot;custom-meta&quot; content=&quot;value&quot;&gt;&#10;&lt;link rel=&quot;stylesheet&quot; href=&quot;custom.css&quot;&gt;"
                                    customCodeSettings={customCodeSettings}
                                />
                                <small className="form-text text-muted">
                                    Add custom code to the &lt;head&gt; section (meta tags, links, etc.).
                                </small>
                            </div>
                        )}

                        {activeTab === 'body' && (
                            <div className="form-group">
                                <label className="dc-label">Body Code</label>
                                <CodeEditor
                                    language="html"
                                    value={customCodeSettings.custom_body_code || ''}
                                    onChange={(value) => handleCodeChange('body_code', value)}
                                    placeholder="<!-- Enter code to be placed in &lt;body&gt; section -->&#10;&lt;script&gt;&#10;    // Custom body scripts&#10;&lt;/script&gt;"
                                    customCodeSettings={customCodeSettings}
                                />
                                <small className="form-text text-muted">
                                    Add custom code to the &lt;body&gt; section.
                                </small>
                            </div>
                        )}

                        {activeTab === 'footer' && (
                            <div className="form-group">
                                <label className="dc-label">Footer Code</label>
                                <CodeEditor
                                    language="html"
                                    value={customCodeSettings.custom_footer_code || ''}
                                    onChange={(value) => handleCodeChange('footer_code', value)}
                                    placeholder="<!-- Enter code to be placed in footer -->&#10;&lt;script&gt;&#10;    // Footer scripts&#10;&lt;/script&gt;"
                                    customCodeSettings={customCodeSettings}
                                />
                                <small className="form-text text-muted">
                                    Add custom code to the footer section.
                                </small>
                            </div>
                        )}
                    </div>
                </div>

                {/* Editor Settings */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h4>Editor Settings</h4>
                    </div>
                    <div className="dc-formtheme dc-userform">
                        <div className="row">
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="editor_theme">Editor Theme</label>
                                    <select
                                        id="editor_theme"
                                        value={customCodeSettings.editor_theme || 'vs-dark'}
                                        onChange={(e) => handleInputChange('editor_theme', e.target.value)}
                                        className="form-control"
                                    >
                                        <option value="vs">Light Theme</option>
                                        <option value="vs-dark">Dark Theme</option>
                                        <option value="hc-black">High Contrast</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="editor_font_size">Font Size</label>
                                    <input
                                        type="number"
                                        id="editor_font_size"
                                        value={customCodeSettings.editor_font_size || 14}
                                        onChange={(e) => handleInputChange('editor_font_size', parseInt(e.target.value))}
                                        className="form-control"
                                        min="10"
                                        max="24"
                                    />
                                </div>
                            </div>
                            <div className="col-md-4">
                                <div className="form-group">
                                    <label htmlFor="editor_tab_size">Tab Size</label>
                                    <input
                                        type="number"
                                        id="editor_tab_size"
                                        value={customCodeSettings.editor_tab_size || 2}
                                        onChange={(e) => handleInputChange('editor_tab_size', parseInt(e.target.value))}
                                        className="form-control"
                                        min="2"
                                        max="8"
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="editor_word_wrap">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Word Wrap
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="editor_word_wrap"
                                            checked={customCodeSettings.editor_word_wrap || false}
                                            onChange={() => handleInputChange('editor_word_wrap', !customCodeSettings.editor_word_wrap)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="editor_line_numbers">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Line Numbers
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="editor_line_numbers"
                                            checked={customCodeSettings.editor_line_numbers || false}
                                            onChange={() => handleInputChange('editor_line_numbers', !customCodeSettings.editor_line_numbers)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Code Optimization */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="dc-tabscontenttitle">
                        <h4>Code Optimization</h4>
                    </div>
                    <div className="dc-formtheme dc-userform">
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="css_minify">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Minify CSS
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="css_minify"
                                            checked={customCodeSettings.css_minify || false}
                                            onChange={() => handleInputChange('css_minify', !customCodeSettings.css_minify)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="js_minify">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Minify JavaScript
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="js_minify"
                                            checked={customCodeSettings.js_minify || false}
                                            onChange={() => handleInputChange('js_minify', !customCodeSettings.js_minify)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="js_async_load">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Async Load JavaScript
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="js_async_load"
                                            checked={customCodeSettings.js_async_load || false}
                                            onChange={() => handleInputChange('js_async_load', !customCodeSettings.js_async_load)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="form-group">
                                    <div className="dc-checkbox-holder" style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '10px',
                                        border: '1px solid #ddd',
                                        borderRadius: '5px',
                                        marginBottom: '10px'
                                    }}>
                                        <label htmlFor="code_validation">
                                            <span className="dc-checkbox-text" style={{ fontWeight: 'bold' }}>
                                                Enable Code Validation
                                            </span>
                                        </label>
                                        <input
                                            type="checkbox"
                                            id="code_validation"
                                            checked={customCodeSettings.code_validation || false}
                                            onChange={() => handleInputChange('code_validation', !customCodeSettings.code_validation)}
                                            className="dc-checkbox"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Save Button */}
                <div className="dc-tabsinfo dc-tabsinfo-mt">
                    <div className="form-group">
                        <button
                            type="button"
                            className="dc-btn dc-btnactive"
                            onClick={handleSave}
                            disabled={isLoading}
                            style={{ padding: '12px 30px', fontSize: '16px' }}
                        >
                            {isLoading ? 'Saving...' : 'Save Custom Code'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CustomCode;
